import { Check } from "lucide-react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  buildQuickFilterValues,
  readQuickFilters,
  toggleQuickFilter,
  type SponsoringQuickFilters as QuickFilterState,
} from "./sponsoringFilters";
import type { SponsoringView } from "./sponsoringViews";
import { getLeadStages, toStageInFilter } from "./stages";

const FILTERS: Array<{ key: keyof QuickFilterState; label: string }> = [
  {
    key: "currentlyActive",
    label: "resources.sponsorings.filters.currently_active",
  },
  { key: "notExpired", label: "resources.sponsorings.filters.not_expired" },
  { key: "endingSoon", label: "resources.sponsorings.filters.ending_soon" },
  { key: "expired", label: "resources.sponsorings.filters.expired" },
  { key: "leads", label: "resources.sponsorings.filters.leads" },
  { key: "showClosed", label: "resources.sponsorings.filters.show_closed" },
  { key: "onlyMine", label: "resources.sponsorings.filters.only_mine" },
];

/**
 * The leads overview already restricts the stages, so every stage chip would
 * be a no-op there: only the owner filter is left.
 */
const LEADS_VIEW_FILTERS = FILTERS.filter(({ key }) => key === "onlyMine");

/**
 * Quick filter chips above the sponsorings table. They read and write the
 * regular list filter values (see sponsoringFilters.ts).
 */
export const SponsoringQuickFilters = ({
  view = "sponsorings",
}: {
  view?: SponsoringView;
}) => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const { identity } = useGetIdentity();
  const { filterValues, setFilters } = useListContext();
  const state = readQuickFilters(
    filterValues,
    toStageInFilter(config.sponsoringActiveStatuses),
    toStageInFilter(getLeadStages(config)),
  );
  const filters = view === "leads" ? LEADS_VIEW_FILTERS : FILTERS;

  const handleToggle = (key: keyof QuickFilterState) => {
    setFilters(
      buildQuickFilterValues({
        config,
        quickFilters: toggleQuickFilter(state, key),
        identityId: identity?.id,
      }),
    );
  };

  return (
    <div
      className="flex flex-nowrap items-center gap-2 mb-4 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-x-visible sm:pb-0"
      role="group"
      aria-label={translate("resources.sponsorings.filters.label")}
    >
      {filters.map(({ key, label }) => {
        const active = state[key];
        return (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={active ? "secondary" : "outline"}
            aria-pressed={active}
            onClick={() => handleToggle(key)}
            className={cn(
              "rounded-full h-8 shrink-0",
              active && "border-transparent",
            )}
          >
            {active ? <Check className="size-3.5" /> : null}
            {translate(label)}
          </Button>
        );
      })}
    </div>
  );
};
